import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { apiCall } from '../hooks/useApi';
import { jsPDF } from 'jspdf';

import html2canvas from 'html2canvas';

const sanitizeFileName = value => (value || '').replace(/[/\\?%*:|"<>]/g, '-').trim();

const getStudentDisplayName = student =>
    (student?.name && student.name.trim()) || `Student ${student?.student_id || ''}`.trim();

const generateStudentPdf = async (student, studentData) => {
    const combinedHtmlContent = studentData?.responses
        ?.map((response) =>
            response.texts
                ?.map(
                    (text) =>
                        `<div style="margin-bottom: 20px;">
                            <h2>Question ${text.question}</h2>
                            ${text.html}
                        </div>`
                )
                .join('')
        )
        .join('');

    const contentHtml = `
        <div style="font-family: Arial, sans-serif; line-height: 1.5; color: #333;">
            <h1 style="border-bottom: 2px solid #333; padding-bottom: 10px;">${getStudentDisplayName(student)}</h1>
            ${combinedHtmlContent || '<p>No responses available.</p>'}
        </div>
    `;

    // Create a temporary container for the HTML
    const container = document.createElement('div');
    container.innerHTML = contentHtml;

    // Position it 'visibly' but underneath everything
    container.style.width = '700px'; // Wider to fill more of the page
    container.style.padding = '30px';
    container.style.position = 'fixed';
    container.style.left = '0';
    container.style.top = '0';
    container.style.zIndex = '-9999';
    container.style.backgroundColor = '#fff';
    document.body.appendChild(container);

    const doc = new jsPDF({
        unit: 'pt',
        format: 'a4',
        orientation: 'portrait'
    });

    try {
        await new Promise((resolve, reject) => {
            doc.html(container, {
                callback: function (pdf) {
                    resolve(pdf);
                },
                x: 20,
                y: 20,
                width: 555, // Target width on PDF
                windowWidth: 700, // Source width
                autoPaging: 'text',
                html2canvas: {
                    scale: 0.75, // Adjust scale to map px to pt
                    logging: false,
                    useCORS: true,
                    allowTaint: true
                }
            });
        });
    } catch (error) {
        console.error("Error generating PDF:", error);
        throw error;
    } finally {
        document.body.removeChild(container);
    }

    return doc.output('blob');
};

export const downloadAllResponses = async (examName, students, setProgress) => {
    const zip = new JSZip();
    const folder = zip.folder(examName.replace(/[/\\?%*:|"<>]/g, '-'));

    let processed = 0;

    // Filter for students with a name (active)
    const activeStudents = students.filter(s => !!s.name);

    if (activeStudents.length === 0) {
        alert('No students with responses found to download.');
        return;
    }

    if (setProgress) setProgress(0);

    // Process students in chunks to avoid overwhelming the browser/API
    const chunkSize = 3; // Reduced chunk size for PDF generation which is more intensive
    for (let i = 0; i < activeStudents.length; i += chunkSize) {
        const chunk = activeStudents.slice(i, i + chunkSize);

        await Promise.all(chunk.map(async (student) => {
            try {
                // Fetch student data
                const studentData = await apiCall('get', '/teacher/student/' + student.student_id);

                // Generate PDF Blob
                const pdfBlob = await generateStudentPdf(student, studentData);

                const fileName = `${sanitizeFileName(getStudentDisplayName(student))}-${student.student_id}.pdf`;
                folder.file(fileName, pdfBlob);
            } catch (error) {
                console.error(`Error processing student ${student.student_id}:`, error);
                folder.file(
                    `${sanitizeFileName(getStudentDisplayName(student))}-${student.student_id}_error.txt`,
                    `Failed to fetch/generate responses for student: ${getStudentDisplayName(student)}. Error: ${error.message}`
                );
            } finally {
                processed++;
                if (setProgress) setProgress(Math.round((processed / activeStudents.length) * 100));
            }
        }));
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, `${examName.replace(/[/\\?%*:|"<>]/g, '-')}_responses.zip`);

    if (setProgress) setTimeout(() => setProgress(null), 1000);
};
